import { useEffect, useRef } from "react";
import { matchPath, resolvePath, useLocation, useNavigate, type NavigateFunction } from "react-router-dom";
import { pathnameOf } from "routing/route";
import { useEphemeralStore } from "state/useEphemeralStore";
import type { PushPathOptions } from "feat/globalRouter/performPush";
import { TRAILING_ROOT_STATE_KEY } from "feat/nativeNavigation/modalRoot";
import { confirmDiscardUnsavedChanges } from "feat/nativeNavigation/useUnsavedChangesGuard";
import { isIosNativeNavigation } from "feat/nativeNavigation/platform";
import { splitEntryTab } from "feat/nativeNavigation/split-entries";
import {
  isEntrySheetStackId,
  isTrailingStackId,
  leadingStackIdOf,
  trailingStackId,
  useSplitStore,
} from "feat/nativeNavigation/split.store";
import { TAB_STACK_IDS, tabOfStack } from "../tab-bar/tab-stack-ids";
import type { ToolbarItemProps } from "../TopToolbar";
import {
  ITEM_ROUTE_PATHS,
  PANE_GROUP_IDS,
  paneRootPath,
  resolveCurrentScreenLayout,
  resolveScreenLayout,
  useScreenLayout,
  type PaneGroupId,
} from "../screen-layout";
import { usePaneSide, type PaneSide } from "./pane-context";

const PANE_DEPTH_KEY = "paneDepth";
const SHEET_BACKGROUND_KEY = "sheetBackground";
const SHEET_ROOT_KEY = "sheetRoot";
const LEADING_ROUTE_KEY = "leadingRoute";
const FROM_OTHER_TAB_KEY = "fromOtherTab";

const depthOf = (state: unknown): number => {
  const depth = (state as Record<string, unknown> | null | undefined)?.[PANE_DEPTH_KEY];
  return typeof depth === "number" ? depth : 0;
};

/** How many screens deep the trailing pane is, from the current history entry. */
const currentPaneDepth = () => depthOf(window.history.state?.usr);

const withState = (options: PushPathOptions | undefined, state: Record<string, unknown>): PushPathOptions => ({
  ...options,
  state: { ...(options?.state ?? {}), ...state },
});

const goBackBy = (nav: NavigateFunction, steps: number) =>
  new Promise<void>((resolve) => {
    window.addEventListener("popstate", () => resolve(), { once: true });
    nav(-steps);
  });

const itemIdOf = (path: string) =>
  ITEM_ROUTE_PATHS.map((pattern) => matchPath(pattern, path)?.params.itemId).find(Boolean);

const stringAt = (state: unknown, key: string): string | undefined => {
  const value = (state as Record<string, unknown> | null | undefined)?.[key];
  return typeof value === "string" ? value : undefined;
};

/**
 * Whether `path` is the entry at `current`. An item's query only says how to show it, so any query
 * on the same item is the same entry; elsewhere the query picks the entry, as Team's sections do.
 */
const isSameEntry = (path: string, current: { pathname: string; search: string }) => {
  const target = new URL(path, window.location.origin);
  if (target.pathname !== current.pathname) return false;
  if (ITEM_ROUTE_PATHS.some((pattern) => matchPath(pattern, target.pathname))) return true;
  const query = (search: string) => [...new URLSearchParams(search)].map(String).sort().join("&");
  return query(target.search) === query(current.search);
};

/** A path as the router resolves it from the current route, such as an address field's "address". */
const absolutePathOf = (path: string) => {
  if (path.startsWith("/")) return path;
  const { pathname, search, hash } = resolvePath(path, window.location.pathname);
  return pathname + search + hash;
};

// The trailing pane's screens with unsaved changes, which a choice in the leading pane would discard,
// each with what it does to discard them.
const unsavedTrailingScreens = new Set<{ discard: () => unknown }>();

/**
 * Marks this screen, while in the trailing pane, as having changes a leading-pane choice would
 * discard. `onDiscard` runs once the user agrees to leave them, such as to drop a stored draft.
 */
export function useTrailingPaneUnsaved(unsaved: boolean, onDiscard?: () => unknown) {
  const side = usePaneSide();
  const onDiscardRef = useRef(onDiscard);
  onDiscardRef.current = onDiscard;
  useEffect(() => {
    if (side !== "trailing" || !unsaved) return;
    const screen = { discard: () => onDiscardRef.current?.() };
    unsavedTrailingScreens.add(screen);
    return () => {
      unsavedTrailingScreens.delete(screen);
    };
  }, [side, unsaved]);
}

/**
 * Before the leading pane navigates away from the trailing pane's screen, asks whether to discard
 * its unsaved changes. Resolves true to go ahead.
 */
export async function confirmLeavingTrailingPane(): Promise<boolean> {
  if (unsavedTrailingScreens.size === 0) return true;
  // Under native navigation a marked screen is in a trailing column by construction, and the
  // main web view's location says nothing about the screens on show.
  if (!isIosNativeNavigation()) {
    const { layout, trailingPane } = resolveCurrentScreenLayout();
    if (layout !== "two-pane" || trailingPane !== "route") return true;
  }
  if (!(await confirmDiscardUnsavedChanges())) return false;
  await Promise.all([...unsavedTrailingScreens].map((screen) => screen.discard()));
  return true;
}

/** The route a sheet was opened over, for the panes to keep showing behind it. */
export const sheetBackgroundOf = (state: unknown) => stringAt(state, SHEET_BACKGROUND_KEY);

/** Whether Back from the current screen closes the sheet it is the first screen of. */
export const backClosesSheet = () =>
  resolveCurrentScreenLayout().sheet === true && window.history.state?.usr?.[SHEET_ROOT_KEY] === true;

/** The list an entry was chosen from, for the leading pane to keep showing beside it. */
export const leadingRouteOf = (state: unknown) => stringAt(state, LEADING_ROUTE_KEY);

/**
 * Adjusts a push for the two-pane layout. Returns the options to push with, or null when the push
 * is handled here. `side` is the pane the push comes from; a push from outside both panes is
 * treated as a choice in the leading pane.
 *
 * - A sheet remembers the route it opens over, and a screen pushed inside it the same route.
 * - A sheet appears in place rather than sliding in; screens pushed inside it slide.
 * - From the leading pane, an entry replaces whatever the trailing pane has open, and on Map an
 *   item with a pin is shown on the map instead.
 * - From the trailing pane, an entry goes one screen deeper.
 * - Either way an entry changes only the trailing pane, so only it slides.
 * - A screen opened from another tab, such as a hotel from the map, or from a screen that takes the
 *   whole window, such as guided create, goes Back to it.
 * - From the leading pane, unsaved changes in the trailing pane are confirmed before they are left.
 */
export async function preparePanePush(
  nav: NavigateFunction,
  side: PaneSide,
  path: string,
  options: PushPathOptions | undefined,
): Promise<PushPathOptions | undefined | null> {
  if (isIosNativeNavigation()) return prepareNativePanePush(path, options);
  const current = resolveCurrentScreenLayout();
  if (current.layout !== "two-pane") {
    // Only a window that allows two panes draws a screen without them, and that one is the
    // whole window: a pane entry opened from it has nothing in its own pane to go Back to.
    const target = resolveCurrentScreenLayout(pathnameOf(absolutePathOf(path)));
    const intoPane = target.layout === "two-pane" && target.trailingPane === "route";
    return intoPane ? withState(options, { [FROM_OTHER_TAB_KEY]: true }) : options;
  }
  path = absolutePathOf(path);
  const target = resolveCurrentScreenLayout(pathnameOf(path));

  if (side !== "trailing" && !isSameEntry(path, window.location)) {
    if (!(await confirmLeavingTrailingPane())) return null;
  }

  if (target.sheet) {
    if (current.sheet) {
      // A screen pushed inside a sheet keeps the route the sheet was opened over.
      const background = sheetBackgroundOf(window.history.state?.usr) || window.location.pathname;
      return withState(options, { [SHEET_BACKGROUND_KEY]: background });
    }
    return {
      ...withState(options, { [SHEET_BACKGROUND_KEY]: window.location.pathname, [SHEET_ROOT_KEY]: true }),
      animation: "none",
    };
  }

  const isEntry =
    target.layout === "two-pane" && target.trailingPane === "route" && target.leadingPane === current.leadingPane;
  if (!isEntry) {
    const fromOtherTab =
      target.layout === "two-pane" && target.trailingPane === "route" && target.leadingPane !== current.leadingPane;
    return fromOtherTab ? withState(options, { [FROM_OTHER_TAB_KEY]: true }) : options;
  }

  const depth = currentPaneDepth();
  const fromElsewhere = openedFromElsewhere();
  const leadingRoute = current.leadingShowsRoute
    ? window.location.pathname + window.location.search
    : leadingRouteOf(window.history.state?.usr);
  const paneState = (paneDepth: number) => ({
    ...withState(options, {
      [PANE_DEPTH_KEY]: paneDepth,
      ...(leadingRoute && { [LEADING_ROUTE_KEY]: leadingRoute }),
      ...(fromElsewhere && { [FROM_OTHER_TAB_KEY]: true }),
    }),
    trailingPaneOnly: true,
  });
  if (side !== "leading") return paneState(depth + 1);

  const itemId = current.trailingPane === "map" ? itemIdOf(path) : undefined;
  if (itemId && useEphemeralStore.getState().selectItineraryComponentById(itemId)) return null;

  // The entry the trailing pane already shows is left as it is, rather than popped and pushed again.
  if (current.trailingPane === "route" && isSameEntry(path, window.location)) return null;

  // A choice in the leading pane is the trailing pane's first screen, which appears in place;
  // only going deeper within the trailing pane slides.
  if (depth > 0) {
    await goBackBy(nav, depth);
  } else if (current.trailingPane === "route") {
    // An entry with nothing behind it in the pane, such as one opened from a link, is replaced
    // and still has nothing behind it, so its Back opens the tab's root.
    nav(path, { replace: true, state: paneState(0).state });
    return null;
  }
  return { ...paneState(1), animation: "none" };
}

/** The pane tab whose split holds `stackId`, and which column it is. */
function splitColumnOf(stackId: string | undefined): { tab: PaneGroupId; trailing: boolean } | undefined {
  if (!stackId) return undefined;
  const trailing = isTrailingStackId(stackId);
  const tab = tabOfStack(trailing ? leadingStackIdOf(stackId) : stackId);
  return tab && PANE_GROUP_IDS.includes(tab) ? { tab, trailing } : undefined;
}

const withNativeTarget = (options: PushPathOptions | undefined, target: string, root?: true): PushPathOptions => {
  // The trailing column's first screen appears in place beside the list. Collapsed it covers the
  // list, which is a push like any other, so it slides.
  const inPlace = root && !useSplitStore.getState().collapsed;
  return {
    ...options,
    ...(inPlace && { animation: "none" as const }),
    state: {
      ...(options?.state ?? {}),
      ...(root && { [TRAILING_ROOT_STATE_KEY]: true }),
      nativeNavigation: {
        ...(options?.state?.nativeNavigation ?? {}),
        target,
        ...(root && { root }),
        ...(inPlace && { animated: false }),
      },
    },
  };
};

/**
 * Where a push lands in a pane tab's native split, from the stack of the screen pushing. The
 * same rules as the web panes, with the native stacks holding what history holds there:
 * - From the leading column, an entry of the tab's list replaces what the trailing column shows,
 *   in place; only going deeper in the trailing column slides.
 * - From the trailing column, an entry goes one screen deeper, and a list leads.
 * - Collapsed, the targets are the same; the plugin shows each where it is seen.
 * - Unsaved changes in the trailing column are confirmed before a leading choice replaces them.
 * - An entry opened from another tab, or from a sheet, opens in its own tab's trailing column;
 *   a chat opens as a sheet over the screen it was opened from instead.
 */
export async function prepareNativePanePush(
  path: string,
  options: PushPathOptions | undefined,
): Promise<PushPathOptions | undefined | null> {
  const column = splitColumnOf(options?.nativeStackId);
  const entryTab = splitEntryTab(absolutePathOf(path));
  // A screen pushed inside a chat opened elsewhere stays in that chat's sheet.
  if (entryTab && column?.tab !== entryTab && !isEntrySheetStackId(options?.nativeStackId)) {
    const { openSplitEntry, presentEntrySheet } = await import("feat/nativeNavigation/sheetNavigation");
    // A chat opened anywhere but Chat, such as from a profile, is a card over that screen and
    // closes back to it, rather than taking the user to the Chat tab.
    const opened =
      entryTab === "chat"
        ? await presentEntrySheet(absolutePathOf(path), options?.state)
        : await openSplitEntry(entryTab, absolutePathOf(path));
    if (opened) return null;
  }
  // Tabs are splits only while the two-pane setting is on.
  if (!column || !useSplitStore.getState().tabsAreSplits) return options;
  const target = resolveScreenLayout({
    width: 0,
    pathname: pathnameOf(absolutePathOf(path)),
    activeTab: column.tab === "edit" ? undefined : column.tab,
    twoPaneSetting: true,
    iosNativeNavigation: true,
    splitCollapsed: false,
  });
  if (target.layout !== "two-pane" || target.sheet || target.leadingPane !== column.tab) return options;

  const leadingStack = TAB_STACK_IDS[column.tab as keyof typeof TAB_STACK_IDS];
  if (!leadingStack) return options;
  const isEntry = target.trailingPane === "route" && !target.leadingShowsRoute;

  if (isEntry && !column.trailing) {
    if (!(await confirmLeavingTrailingPane())) return null;
    return withNativeTarget(options, trailingStackId(leadingStack), true);
  }
  if (!isEntry && column.trailing) return withNativeTarget(options, leadingStack);
  return options;
}

/** Tells whether the trailing pane shows an entry of the leading pane's list, for the list to highlight it. */
export function useShowsInTrailingPane(): (path: string) => boolean {
  const location = useLocation();
  const { layout, trailingPane } = useScreenLayout();
  const shows = layout === "two-pane" && trailingPane === "route";
  return (path) => shows && isSameEntry(path, location);
}

/** The tab root that Back and Close return the trailing pane to, when history cannot. */
function paneRootFor(side: PaneSide | null): string | undefined {
  if (side !== "trailing") return undefined;
  const { layout, trailingPane, leadingPane, sheet } = resolveCurrentScreenLayout();
  if (layout !== "two-pane" || trailingPane !== "route" || sheet || !leadingPane) return undefined;
  return paneRootPath(leadingPane);
}

/**
 * Whether the panes were opened from another tab, or from a screen that takes the whole window
 * such as guided create. Every screen pushed in them since carries the mark.
 */
export const openedFromElsewhere = () => window.history.state?.usr?.[FROM_OTHER_TAB_KEY] === true;

/**
 * How far Back goes from the leading pane while it shows a list with one of its entries beside
 * it, or panes opened from elsewhere: past the entry's screens and the list, to wherever the list
 * or the panes were opened from.
 */
export function leadingBackSteps(side: PaneSide | null): number | undefined {
  // Native stacks hold the panes' history under native navigation.
  if (isIosNativeNavigation()) return undefined;
  const state = window.history.state?.usr;
  if (side !== "leading" || !(leadingRouteOf(state) || openedFromElsewhere())) return undefined;
  return currentPaneDepth() + 1;
}

/**
 * Where Back goes from the trailing pane when it has no screens of its own to go back through,
 * such as an entry opened from a link. Null when Back follows history.
 */
export function paneBackPath(side: PaneSide | null): string | null {
  // Native back runs through the column's own stack under native navigation.
  if (isIosNativeNavigation()) return null;
  if (currentPaneDepth() > 0 || openedFromElsewhere()) return null;
  return paneRootFor(side) ?? null;
}

/**
 * Closes the trailing pane's screens: back through them, then to the tab's root if that lands on
 * an entry with nothing behind it, such as one opened from a link.
 */
async function closePane(nav: NavigateFunction, depth: number) {
  await goBackBy(nav, depth);
  // Like a choice in the leading pane, the pane's own content appears in place.
  const root = paneBackPath("trailing");
  if (root) nav(root, { replace: true });
}

/**
 * Whether Back from `side` leaves the trailing pane showing the tab's own content again: from the
 * pane's first entry, chosen in the leading pane. That appears in place rather than sliding, as
 * the entry did.
 */
export function backClosesTrailingEntry(side: PaneSide | null): boolean {
  if (side !== "trailing" || isIosNativeNavigation() || !paneRootFor(side)) return false;
  return currentPaneDepth() === 1 && !openedFromElsewhere();
}

/**
 * How a screen's navigation bar behaves in the trailing pane: Back shows on every entry and
 * returns the pane to what it showed before, and is hidden on the tab's own content, such as
 * Profile. From two screens deep a Close item returns to the tab's root, except while editing,
 * where each screen is left by its own Done or Back.
 */
export function usePaneNavigationBar(): { hidesBack: boolean; closeItem?: ToolbarItemProps } {
  const side = usePaneSide();
  const { state } = useLocation();
  const nav = useNavigate();
  // The native bar carries Back for each column's own stack.
  if (side !== "trailing" || isIosNativeNavigation()) return { hidesBack: false };
  const depth = depthOf(state);
  const root = paneRootFor(side);
  const closeItem =
    depth >= 2 && root && resolveCurrentScreenLayout().leadingPane !== "edit"
      ? { id: "paneClose", icon: "Cross" as const, action: () => void closePane(nav, depth) }
      : undefined;
  // With nothing of its own behind it, an entry's Back opens the tab's root (`paneBackPath`).
  return { hidesBack: depth === 0 && !root, closeItem };
}
